import React, { useEffect, useState, useRef } from 'react';
import { Box, Container, Grid, Typography, Button, Stack } from '@mui/material';
import { WhatsApp, CheckCircleOutline, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const features = [
  {
    title: "Expert Physiotherapists",
    description: "Our team consists of highly qualified and experienced physiotherapy professionals"
  },
  {
    title: "Personalized Care",
    description: "Tailored treatment plans designed specifically for your unique needs"
  },
  {
    title: "Modern Facilities",
    description: "State-of-the-art equipment and comfortable treatment environment"
  },
  {
    title: "Evidence-Based Treatment",
    description: "We follow the latest research and proven therapeutic techniques"
  },
  {
    title: "Holistic Approach",
    description: "Comprehensive care addressing both symptoms and root causes"
  },
  {
    title: "Continuous Support",
    description: "Ongoing guidance and support throughout your recovery journey"
  }
];

const AboutSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={sectionRef}
      component="section"
      id="about-us"
      sx={{
        py: { xs: 10, md: 15 },
        background: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(0,150,136,0.05) 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: 'relative',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: { xs: 2, sm: 3, md: 4 },
                mb: { xs: 6, md: 0 },
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0)' : 'translateX(-50px)',
                transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
              }}
            >
              <Box
                sx={{
                  gridColumn: '1 / -1',
                  position: 'relative',
                  aspectRatio: '4/3',
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: 'opacity 0.6s ease-out 0.2s, transform 0.6s ease-out 0.2s',
                  mb: { xs: 3, md: 4 },
                }}
              >
                <Box
                  component="img"
                  src="/homecare.png"
                  alt="Professional physiotherapist providing home care treatment"
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '24px',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                    position: 'relative',
                    zIndex: 2,
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.03)',
                      boxShadow: '0 25px 50px rgba(0,0,0,0.2)',
                    }
                  }}
                />
              </Box>
              <Box
                sx={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: 'opacity 0.6s ease-out 0.4s, transform 0.6s ease-out 0.4s',
                  height: { xs: '220px', sm: '250px', md: '280px' },
                }}
              >
                <Box
                  component="img"
                  src="/Manual Therapy.png"
                  alt="Manual therapy techniques demonstration"
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '20px',
                    boxShadow: '0 15px 30px rgba(0,0,0,0.12)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.08) rotate(2deg)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.18)',
                    }
                  }}
                />
              </Box>
              <Box
                sx={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: 'opacity 0.6s ease-out 0.6s, transform 0.6s ease-out 0.6s',
                  height: { xs: '220px', sm: '250px', md: '280px' },
                }}
              >
                <Box
                  component="img"
                  src="/ortho.png"
                  alt="Orthopedic physiotherapy session"
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '20px',
                    boxShadow: '0 15px 30px rgba(0,0,0,0.12)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.08) rotate(-2deg)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.18)',
                    }
                  }}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                pl: { md: 4, lg: 6 },
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0)' : 'translateX(50px)',
                transition: 'opacity 0.6s ease-out 0.2s, transform 0.6s ease-out 0.2s',
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 700,
                  letterSpacing: '4px',
                  mb: 3,
                  display: 'block',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'all 0.6s ease-out 0.3s',
                  textShadow: '0 2px 10px rgba(0,150,136,0.1)',
                  position: 'relative',
                  paddingBottom: 1,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: isVisible ? '50px' : '0',
                    height: '2px',
                    background: 'linear-gradient(90deg, #009688, transparent)',
                    transition: 'width 0.6s ease-out 0.6s'
                  }
                }}
              >
                HOME CARE SERVICES
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2.2rem', sm: '2.5rem', md: '3.2rem' },
                  fontWeight: 800,
                  mb: 5,
                  background: 'linear-gradient(45deg, #009688 30%, #4CAF50 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  lineHeight: { xs: 1.3, md: 1.2 },
                  letterSpacing: '-0.5px',
                  maxWidth: '90%',
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'all 0.6s ease-out 0.4s',
                  textShadow: '0 5px 15px rgba(0,150,136,0.1)',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-10px',
                    left: '-20px',
                    width: '40px',
                    height: '40px',
                    background: 'radial-gradient(circle, rgba(0,150,136,0.1) 0%, transparent 70%)',
                    borderRadius: '50%',
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'scale(1)' : 'scale(0)',
                    transition: 'all 0.6s ease-out 0.5s'
                  }
                }}
              >
                Professional Home Care Physiotherapy
              </Typography>

              <Box
                sx={{
                  p: { xs: 3, sm: 4, md: 5 },
                  background: 'rgba(255,255,255,0.95)',
                  borderRadius: { xs: '20px', md: '24px' },
                  boxShadow: '0 10px 40px rgba(0,0,0,0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(0,150,136,0.1)',
                  mb: 5,
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: 'all 0.6s ease-out 0.5s',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #009688, #4CAF50)',
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'scaleX(1)' : 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.8s ease-out 0.7s, opacity 0.3s',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: '150px',
                    height: '150px',
                    background: 'radial-gradient(circle, rgba(0,150,136,0.03) 0%, transparent 70%)',
                    borderRadius: '50%',
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'scale(1)' : 'scale(0)',
                    transition: 'all 0.8s ease-out 1s',
                  },
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 15px 45px rgba(0,150,136,0.1)',
                    '&::after': {
                      transform: 'scale(1.2)',
                    }
                  }
                }}
              >
                {[
                  {
                    text: "At Careflex Physiotherapy, we bring expert physiotherapy care directly to your doorstep. Our dedicated team of skilled physiotherapists specializes in providing personalized, compassionate, and professional treatment in the comfort of your own home, ensuring that your recovery journey is as convenient and comfortable as possible.",
                    highlight: "Careflex Physiotherapy"
                  },
                  {
                    text: "We understand that mobility challenges can make clinic visits difficult. That's why we've designed our services to come to you, whether you're recovering from surgery, managing chronic pain, or seeking to improve your mobility and independence. Our comprehensive home care approach ensures you receive the highest quality physiotherapy treatment without the stress of travel.",
                    highlight: "mobility challenges"
                  },
                  {
                    text: "Experience the convenience and effectiveness of professional physiotherapy in your home environment. Our expert therapists bring all necessary equipment and expertise to deliver a full range of treatments, from manual therapy to rehabilitation exercises, ensuring your path to recovery is both comfortable and successful.",
                    highlight: "convenience and effectiveness"
                  }
                ].map((paragraph, index) => (
                  <Typography
                    key={index}
                    variant="body1"
                    sx={{
                      position: 'relative',
                      color: 'text.primary',
                      mb: index === 2 ? 0 : 4,
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      lineHeight: { xs: 1.7, sm: 1.8 },
                      letterSpacing: '0.3px',
                      textAlign: 'justify',
                      fontFamily: "'Poppins', sans-serif",
                      px: { xs: 0, sm: 1 },
                      py: 1,
                      opacity: isVisible ? 1 : 0,
                      transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                      transition: `all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) ${0.6 + index * 0.2}s`,
                      background: 'linear-gradient(90deg, rgba(0,150,136,0.03) 0%, transparent 100%)',
                      borderRadius: '12px',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: '-10px',
                        top: '50%',
                        width: '3px',
                        height: isVisible ? '100%' : '0%',
                        background: 'linear-gradient(180deg, #009688, transparent)',
                        transform: 'translateY(-50%)',
                        transition: `height 0.6s ease-out ${0.9 + index * 0.2}s`,
                        opacity: 0.3,
                        borderRadius: '3px',
                      },
                      '& strong': {
                        color: 'primary.main',
                        fontWeight: 700,
                        position: 'relative',
                        display: 'inline-block',
                        padding: '0 4px',
                        transition: 'all 0.3s ease',
                        background: 'rgba(0,150,136,0.05)',
                        borderRadius: '4px',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: '-2px',
                          left: 0,
                          width: '100%',
                          height: '2px',
                          background: 'linear-gradient(90deg, #009688, transparent)',
                          opacity: 0.3,
                          transform: 'scaleX(0)',
                          transformOrigin: 'left',
                          transition: 'transform 0.4s ease-out',
                        },
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          background: 'rgba(0,150,136,0.08)',
                          '&::after': {
                            transform: 'scaleX(1)',
                          }
                        }
                      },
                      '&:hover': {
                        transform: 'translateX(8px)',
                        background: 'linear-gradient(90deg, rgba(0,150,136,0.05) 0%, transparent 100%)',
                        transition: 'all 0.3s ease-out',
                        '&::before': {
                          height: '80%',
                          opacity: 0.5,
                        }
                      }
                    }}
                  >
                    {paragraph.text.split(paragraph.highlight).map((part, i, arr) => (
                      <React.Fragment key={i}>
                        <span style={{
                          opacity: isVisible ? 1 : 0,
                          transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
                          transition: `all 0.5s ease-out ${0.8 + i * 0.1 + index * 0.2}s`,
                          display: 'inline-block',
                          position: 'relative',
                          zIndex: 1,
                        }}>
                          {part}
                        </span>
                        {i < arr.length - 1 && (
                          <strong style={{
                            opacity: isVisible ? 1 : 0,
                            transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
                            transition: `all 0.5s ease-out ${1 + i * 0.1 + index * 0.2}s`,
                            position: 'relative',
                            zIndex: 2,
                          }}>
                            {paragraph.highlight}
                          </strong>
                        )}
                      </React.Fragment>
                    ))}
                  </Typography>
                ))}
              </Box>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 2, sm: 3 }}
                sx={{
                  mt: { xs: 4, sm: 5 },
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: 'opacity 0.6s ease-out 0.6s, transform 0.6s ease-out 0.6s',
                  '& .MuiButton-root': {
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 3, sm: 4 },
                    borderRadius: '50px',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    fontWeight: 600,
                    flex: 1,
                    textTransform: 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => navigate('/book-appointment')}
                  sx={{
                    background: 'linear-gradient(45deg, #009688, #4CAF50)',
                    boxShadow: '0 4px 20px rgba(0,150,136,0.3)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #00897B, #43A047)',
                      transform: 'translateY(-5px)',
                      boxShadow: '0 8px 25px rgba(0,150,136,0.4)'
                    }
                  }}
                >
                  Book Home Visit
                </Button>

                <Button
                  variant="outlined"
                  component="a"
                  href="tel:+917619678243"
                  sx={{
                    borderWidth: 2,
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: 'primary.dark',
                      backgroundColor: 'rgba(0,150,136,0.05)',
                      transform: 'translateY(-5px)'
                    }
                  }}
                >
                  Call Now
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default AboutSection; 